<template>
  <header class="product-header" :class="{ 'disable-img-background': disableBackgroundImage }">
    <div class="product-basic-info">
      <div class="product-header-left zone-image">
        <div class="product-header-left-badges">
          <adn-tag
            v-if="isAuthenticated && isExcluTech && !isFrontAgri"
            background-color="var(--color-coop)"
            font-size="var(--font-size-xs)"
            font-weight="bold"
            text-color="white"
            shape="0.4rem"
            border-style="none"
            data-test-exclu-tech-badge
          >
            {{ i18n._('Exclu {partnerType}', { partnerType }) }}
          </adn-tag>
          <adn-tag
            v-if="product && product.isSupplierHighlighted"
            background-color="var(--color-main)"
            font-size="var(--font-size-xs)"
            font-weight="bold"
            text-color="white"
            shape="0.4rem"
            border-style="none"
            data-test="supplier-logo-highlighted"
            :class="tagClassInnovation"
          >
            {{ i18n._('Innovation') }}
          </adn-tag>
        </div>
        <!-- Composite Image -->
        <c-product-header-gallery :product-id="productId"></c-product-header-gallery>
      </div>

      <!-- Summary -->
      <section class="summary zone-summary">
        <header class="summary-heading">
          <c-cooperative-insight
            class="zone-insights"
            :product="product"
            :cooperative-name="cooperativeName"
          ></c-cooperative-insight>
          <h1 class="title" data-test="product-name">{{ productNameUpperCase }}</h1>
          <slot name="header-supplier">
            <c-supplier-logo
              class="product-header__supplier-logo"
              :product="product"
              @click="trackClickOnSupplierLogo()"
              @go-to-product-search="goToProductSearch($event.categoryCode, $event.supplierName)"
            ></c-supplier-logo>
            <div class="supplier-link-container">
              <a
                v-if="isAladin && product && product.supplierUniversePageLink && product.supplier"
                @click="trackClickOnSupplierLink()"
                class="supplier-link"
                :href="product.supplierUniversePageLink"
                target="_blank"
              >
                {{ i18n._('Découvrir {productSupplier}', { productSupplier: product.supplier }) }}
                <span class="adn-icon adn-icon--navigation-direction-right" aria-hidden="true" />
              </a>
            </div>
          </slot>
          <slot name="header-market-authorization"></slot>

          <adn-layout-horizontal-list :items="productCategories" v-slot="{ item }" mobile-container-overflow>
            <!-- @TODO: Replace hexa color with the future design tokens -->
            <adn-tag name="tag-name" border-color="#9BA6AC" class="category-tag">
              <span @click="goToProductSearch(item.code)">
                {{ item?.label }}
              </span>
            </adn-tag>
          </adn-layout-horizontal-list>

          <slot name="header-details"></slot>
        </header>
        <p-see-more-price
          v-if="isAuthenticated && hasOffers"
          :scroll-to="'#product-detail__offer-list'"
          :has-special-offer="hasBestOffer && hasSpecialOffer && hasOffers"
          :is-authenticated="isAuthenticated"
        >
          <template #priceFrom>
            <p-product-best-price :product-id="productId" />
          </template>
        </p-see-more-price>
        <div v-if="!isAuthenticated" data-test="summary-not-authenticated">
          <p class="summary_not_authenticated">
            <span class="adn-icon adn-icon--status-check" aria-hidden="true" />
            {{ i18n._('Payer avec votre compte partenaire') }}
          </p>
        </div>
        <p-unprocessable-product
          :has-no-offer="hasNoOffer"
          :is-not-sold-by-coop="isNotSoldByCoop"
          :product-generic-label="productGenericLabel"
          :cooperative-name="cooperativeName"
          :product="product"
          :is-front-agri="isFrontAgri"
          v-if="hasNoOffer || isNotSoldByCoop"
        />
      </section>
    </div>
  </header>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'
import { useProduct } from '@b2ag/catalog/src/useProduct'
import { useAnalyticService } from '@b2ag/plugin-aladin-gtm'
import AdnLayoutHorizontalList from '@b2ag/design-system/src/layouts/AdnLayoutHorizontalList/AdnLayoutHorizontalList.vue'
import AdnTag from '@b2ag/design-system/src/components/AdnTag/AdnTag.vue'
import { isAladin, isEpisens, isFarmi, isUMG_US } from '@b2ag/app-brands'
import { toUpperCaseExceptMicron } from '@b2ag/utils/src/toUppercaseExceptMicron'
import { getAppTypeLocale, i18n } from '@b2ag/locale'
import PSeeMorePrice from '../PSeeMorePrice/PSeeMorePrice.vue'
import { getProductGenericLabel, hasOffers } from '../../services/product.business'
import PUnprocessableProduct from '../PUnprocessableProduct/PUnprocessableProduct.vue'
import PProductBestPrice from '../PProductPriceFromProductPage/PProductBestPrice.vue'
import CCooperativeInsight from './CCooperativeInsight/CCooperativeInsight.vue'
import CProductHeaderGallery from './CProductHeaderGallery/CProductHeaderGallery.vue'
import CSupplierLogo from './CSupplierLogo/CSupplierLogo.vue'
import { currentAppBrand } from '@b2ag/app-brands'

export default defineComponent({
  name: 'PProductHeader',
  components: {
    AdnLayoutHorizontalList,
    AdnTag,
    PSeeMorePrice,
    PUnprocessableProduct,
    PProductBestPrice,
    CCooperativeInsight,
    CProductHeaderGallery,
    CSupplierLogo,
  },
  props: {
    productId: {
      type: String,
      required: true,
    },
    isAuthenticated: {
      type: Boolean,
      default: false,
    },
    cooperativeName: {
      type: String,
      default: '',
    },
    isFrontAgri: {
      type: Boolean,
      default: true,
    },
    isAladin: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { product, isOffSeasonProduct, bestOffer, isExcluTech } = useProduct(props.productId)

    const hasOffersComputed = computed(() => hasOffers(product.value))
    const hasNoOffer = computed(() => product.value?.isCooperativeProduct && !hasOffersComputed.value)
    const productGenericLabel = computed(() => getProductGenericLabel(product))
    const isNotSoldByCoop = computed(() => props.isAuthenticated && !product.value?.isCooperativeProduct)
    const productCategories = computed(() =>
      product.value?.categories
        .flat()
        .filter((category) => category.code !== product.value?.kind)
        .filter((value, index, self) => self.indexOf(self.find((v) => v.code === value.code)!) === index),
    )

    const hasBestOffer = computed(() => !!(product.value?.isCooperativeProduct && product.value?.bestOffer))
    const hasSpecialOffer = computed(() => isOffSeasonProduct.value || bestOffer.value?.hasDiscount())
    const isSupporterSupplier = computed(() => product.value?.supplierScore <= 20)

    // à remplacer plus tard par les variables de thème (les gradients ne sont pas gérés actuellement)
    const tagClassInnovation = computed(() => {
      if (isAladin) {
        return 'tag--innovation-blue'
      }
      if (isFarmi) {
        return 'tag--innovation-green'
      }
      return 'tag--innovation-none'
    })

    const productNameUpperCase = computed(() => toUpperCaseExceptMicron(product.value ? product.value?.name : ''))

    const trackClickOnSupplierLink = () => {
      useAnalyticService().trackEvent('click_link_PU', {})
    }
    const trackClickOnSupplierLogo = () => {
      useAnalyticService().trackEvent('click_logo_PU', {})
    }

    const goToProductSearch = (categoryCode, supplierName?) => {
      emit('goToProductSearch', { categoryCode, supplierName })
    }

    const appTypeLocale = getAppTypeLocale(currentAppBrand)
    const partnerType = computed(() => appTypeLocale.tc)

    return {
      isOffSeasonProduct,
      product: product as any /* @ts-ignore d16n:legacy product mapping */,
      bestOffer,
      hasOffers: hasOffersComputed,
      productGenericLabel,
      isNotSoldByCoop,
      productCategories,
      productNameUpperCase,
      hasBestOffer,
      hasSpecialOffer,
      isSupporterSupplier,
      goToProductSearch,
      hasNoOffer,
      trackClickOnSupplierLink,
      trackClickOnSupplierLogo,
      isExcluTech,
      tagClassInnovation,
      partnerType,
      i18n,
      disableBackgroundImage: isFarmi || isEpisens || isUMG_US,
    }
  },
})
</script>

<style lang="scss" scoped>
.product-header {
  position: relative;

  @media (max-width: ($breakpoint-xl - 1)) {
    margin: 0 calc((100vw - 100%) / -3);
  }

  @media (max-width: ($breakpoint-md - 1)) {
    margin: 0 auto;
  }
}

.product-basic-info {
  position: relative;
  background-color: var(--color-white);
  padding: var(--spacing-5);
  grid-template-areas: 'zone-image' 'zone-summary';
  grid-template-rows: max-content;
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    pointer-events: none;
  }

  &::after {
    background: bottom 0% right
      url('@b2ag/themes/aladin/assets/images/product-background/product-background-mobile-bottom-right.svg') no-repeat;
    width: 63%;
    height: 45%;
    content: '';
    position: absolute;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: -1;
  }

  & > .cooperative-insight-list {
    position: relative;
    z-index: 10;
  }

  @media (min-width: $breakpoint-md) {
    grid-gap: 1rem;
    padding-bottom: 2rem;
    background-color: var(--color-white);
    border-radius: var(--border-radius-4);
    grid-template-areas: 'zone-image' 'zone-summary';
    grid-template-rows: max-content;

    &::before {
      background: url('@b2ag/themes/aladin/assets/images/product-background/product-background-mobile-top-left.svg')
        no-repeat;
      background-size: cover;
      width: 45%;
      height: 50%;
      content: '';
      position: absolute;
      top: 0;
      border-top-left-radius: 2.4rem;
      z-index: 2;
    }

    &::after {
      background: bottom 0% right
        url('@b2ag/themes/aladin/assets/images/product-background/product-background-tablet-bottom-right.svg') no-repeat;
      width: 55%;
      height: 45%;
      content: '';
      position: absolute;
      right: 0;
      bottom: 0;
      border-bottom-right-radius: 2.4rem;
    }
  }

  @media (min-width: $breakpoint-lg) {
    display: grid;
    background-color: var(--color-white);
    grid-template-columns: 40rem auto;
    grid-template-areas: 'zone-image zone-summary';
    grid-template-rows: max-content;

    &::before {
      background: right top
        url('@b2ag/themes/aladin/assets/images/product-background/product-background-desktop-top-right.svg') no-repeat;
      width: 30%;
      height: 70%;
      content: '';
      position: absolute;
      right: 0;
      border-top-right-radius: 2.4rem;
    }

    &::after {
      background: left bottom
        url('@b2ag/themes/aladin/assets/images/product-background/product-background-desktop-bottom-left.svg') no-repeat;
      width: 40%;
      height: 50%;
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      border-bottom-left-radius: 2.4rem;
    }
  }

  > *:not(:last-child) {
    @media (min-width: $breakpoint-md) {
      margin-bottom: 0;
    }
  }
}

.product-header-left {
  display: flex;
  flex-direction: column;
  width: 100%;
  row-gap: var(--spacing-3);

  &-badges {
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;

    div {
      margin-right: var(--spacing-1);
    }

    div:nth-child(n + 4) {
      display: none;
    }

    @media ((min-width: $breakpoint-md) and (max-width: $breakpoint-lg)) {
      justify-content: flex-end;

      div {
        margin-left: var(--spacing-1);
        margin-right: 0;
      }
    }
  }

  .tag {
    padding: 0.2rem 0.8rem;
    letter-spacing: 0.04rem;
  }

  .tag--innovation-blue {
    @include apply-linear-gradient((var(--gradient-main-start), var(--gradient-main-end)), 90deg);
  }

  .tag--innovation-green {
    @include apply-linear-gradient((var(--gradient-green-start), var(--gradient-green-end)), 90deg);
  }
}

.product-header.disable-img-background {
  .product-basic-info::after,
  .product-basic-info::before {
    background: none;
  }
}

.summary {
  width: 100%;
  margin: 0;
  display: flex;
  flex-direction: column;
  min-width: 0; // Avoid overflowing content from enlarging flex cell

  @media (min-width: $breakpoint-md) {
    padding: 0;
    border-bottom-right-radius: 1rem;
  }

  @media (min-width: $breakpoint-lg) {
    padding-left: 2.5rem;
  }
}

.summary-heading {
  margin: var(--spacing-4) 0 15px;
  position: relative;
  z-index: 10;

  @media (min-width: $breakpoint-md) {
    margin-top: 0;
  }
}

.summary_not_authenticated {
  color: #71838c;
  margin-top: var(--spacing-2);
}

.adn-icon--status-check {
  color: var(--color-green);
}

.category-tag {
  cursor: pointer;
}

.title {
  color: var(--color-black);
  display: block;
  margin: 0;
  font-weight: bold;
  min-height: 1em;
  font-size: var(--font-size-m);
  word-wrap: break-word;

  @media (min-width: $breakpoint-sm) {
    font-size: var(--font-size-xxl);
  }
}

.zone-insights {
  grid-area: zone-insights;
}

.zone-summary {
  grid-area: zone-summary;
}

.zone-image {
  grid-area: zone-image;
  justify-self: center;
  height: fit-content;
  display: flex;
  align-items: center;
  min-height: 330px;

  @media screen and (min-width: $breakpoint-sm) {
    min-height: 400px;
  }
}

.supplier-link-container {
  margin-top: var(--spacing-1);
  margin-bottom: var(--spacing-3);
}

.supplier-link {
  color: var(--color-grey-dark);
  display: flex;
  align-items: center;

  span {
    font-size: var(--font-size-m);
    font-weight: bold;
    padding: 4px;
    transition: margin-left 200ms ease-out;
  }

  &:hover span {
    margin-left: 4px;
  }
}
</style>
